import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, KeyRound, Wallet, Timer, CheckCircle2, IndianRupee, Play, Square,
  Armchair, MapPin, RefreshCw, ClipboardCheck, MonitorPlay, Wrench, AlertTriangle,
  History, Search as SearchIcon,
} from "lucide-react";
import { toast } from "sonner";
import { StaffMaintenance } from "./Maintenance";

export default function Staff() {
  return (
    <DashboardShell title="Staff Dashboard" actions={
      <Link to="/board"><Button variant="outline" size="sm" className="gap-1.5 gold-border text-gold" data-testid="open-board-btn"><MonitorPlay size={15} /><span className="hidden sm:inline">Chair Board</span></Button></Link>
    }>
      <Tabs defaultValue="payments">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start bg-card">
          <TabsTrigger value="payments" data-testid="tab-payments"><Wallet size={15} className="mr-1.5" />Payment Queue</TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active"><Timer size={15} className="mr-1.5" />Active Sessions</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History size={15} className="mr-1.5" />Order History</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench size={15} className="mr-1.5" />Maintenance</TabsTrigger>
        </TabsList>
        <TabsContent value="payments"><PaymentQueue /></TabsContent>
        <TabsContent value="active"><ActiveSessions /></TabsContent>
        <TabsContent value="history"><OrderHistory /></TabsContent>
        <TabsContent value="maintenance"><StaffMaintenance /></TabsContent>
      </Tabs>
    </DashboardShell>
  );
}

function VerifyStart() {
  const [code, setCode] = useState("");
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/staff/verify-code", { code: code.trim() });
      setDetails(data);
    } catch (e) {
      setDetails(null);
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/staff/start-session", { code: details.billing_code });
      toast.success("Session started! Check Active Sessions.");
      setDetails(null);
      setCode("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg">
      <div className="rounded-2xl bg-card p-7 gold-border">
        <h2 className="font-serif text-xl font-bold">Start a Massage</h2>
        <p className="mt-1 text-sm text-muted-foreground">Enter the customer's billing code to verify their payment.</p>
        <div className="mt-5">
          <Label>Billing Code</Label>
          <div className="mt-1.5 flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="KPS-XXXXXX-XXXX"
              className="font-mono uppercase" data-testid="verify-code-input" onKeyDown={(e) => e.key === "Enter" && verify()} />
            <Button onClick={verify} disabled={busy} className="bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid="verify-code-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
            </Button>
          </div>
        </div>

        {details && (
          <div className="mt-6 animate-fade-up rounded-xl bg-secondary/40 p-5" data-testid="verify-result">
            <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 size={18} /><span className="font-semibold">Payment {details.payment_status}</span></div>
            <div className="mt-4 space-y-2 text-sm">
              <Row l="Customer" v={details.customer_name} />
              <Row l="Service" v={`${details.duration_minutes} min · ${details.service_name}`} />
              <Row l="Eye Massager" v={details.eye_massager_selected ? <span className="font-semibold text-emerald-400">YES</span> : "No"} />
              <Row l="Base Price" v={<span className="flex items-center"><IndianRupee size={13} />{details.base_service_price ?? details.amount}</span>} />
              {details.eye_massager_selected && <Row l="Add-On" v={<span className="flex items-center text-emerald-400">Eye Massager +<IndianRupee size={12} />{details.eye_massager_price}</span>} />}
              <Row l="Total Paid" v={<span className="flex items-center gold-text font-semibold"><IndianRupee size={13} />{details.total_amount ?? details.amount}</span>} />
              <Row l="Chair" v={details.chair_code} />
              <Row l="Payment" v={details.payment_method.toUpperCase()} />
              <Row l="Status" v={<span className="text-emerald-400">Ready to Start</span>} />
            </div>
            <Button onClick={start} disabled={busy} className="mt-5 h-12 w-full gap-2 bg-emerald-600 font-semibold text-white hover:bg-emerald-700" data-testid="start-session-btn">
              <Play size={18} /> Start Massage
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentQueue() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(() => {
    api.get("/staff/pending-payments")
      .then((r) => { setOrders(r.data); setLoadError(null); })
      .catch((e) => { setLoadError(apiError(e, "Could not load pending payments. Check your connection or log in again.")); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, [load]);

  const confirm = async (o) => {
    setConfirming(o.id);
    let confirmed = null;
    try {
      const { data } = await api.post(`/staff/confirm-payment/${o.id}`, {});
      confirmed = data;
    } catch (e) {
      toast.error(apiError(e));
      setConfirming(null);
      load();
      return;
    }
    // Payment is now confirmed. Try to start the session on the chair.
    try {
      await api.post("/staff/start-session", { code: confirmed.billing_code });
      toast.success(`Payment confirmed — session started for ${o.customer_name}`);
      if (confirmed.bonus_billing_code) toast.success(`Bonus free session code: ${confirmed.bonus_billing_code}`);
    } catch (e) {
      // Money is confirmed but the chair could not be started (e.g. still busy).
      // Surface the billing code so staff can start it via "Start by code" once free.
      toast.error(
        `Payment confirmed for ${o.customer_name}, but the session could not start (${apiError(e)}). Billing code: ${confirmed.billing_code}. Free the chair, then start it from "Start a paid session by code" below.`,
        { duration: 15000 }
      );
    } finally {
      setConfirming(null);
      load();
    }
  };

  if (loading) return <Center><Loader2 className="h-6 w-6 animate-spin gold-text" /></Center>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-xl font-bold">Pending Payments <span className="text-gold">({orders.length})</span></h2>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5 gold-border text-gold"><RefreshCw size={14} /> Refresh</Button>
      </div>
      {loadError && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="queue-error">
          <span className="flex items-center gap-2"><AlertTriangle size={15} /> {loadError}</span>
          <Button size="sm" variant="outline" onClick={load} className="border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="queue-retry-btn">Retry</Button>
        </div>
      )}
      {orders.length === 0 ? (
        !loadError && <EmptyState icon={ClipboardCheck} text="No payments waiting for confirmation." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {orders.map((o) => (
            <div key={o.id} className="rounded-2xl bg-card p-5 gold-border card-hover" data-testid={`pending-order-${o.id}`}>
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${o.payment_method === "cash" ? "bg-emerald-500/15 text-emerald-400" : "bg-violet-500/15 text-violet-400"}`}>
                  {o.payment_method.toUpperCase()}
                </span>
                <span className="flex items-center text-lg font-bold gold-text"><IndianRupee size={15} />{o.amount}</span>
              </div>
              <div className="mt-3 font-semibold">{o.customer_name}</div>
              <div className="text-xs text-muted-foreground">{o.customer_mobile}</div>
              {o.eye_massager_selected && <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-400" data-testid={`pending-eye-${o.id}`}>👁 Eye Massager +₹{o.eye_massager_price}</div>}
              <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Armchair size={12} />{o.chair_code}</span>
                <span className="flex items-center gap-1"><Timer size={12} />{o.duration_minutes} min</span>
              </div>
              <Button onClick={() => confirm(o)} disabled={confirming === o.id}
                className="mt-4 w-full gap-1.5 bg-gold font-semibold text-primary-foreground hover:bg-gold-hover" data-testid={`confirm-payment-${o.id}`}>
                {confirming === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 size={16} /> Confirm & Start</>}
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 border-t border-border pt-6">
        <details data-testid="start-by-code-section">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-gold">Start a paid session by billing code</summary>
          <div className="mt-4"><VerifyStart /></div>
        </details>
      </div>
    </div>
  );
}

function ActiveSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stopTarget, setStopTarget] = useState(null);
  const [reason, setReason] = useState("Customer requested early stop");
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    api.get("/staff/sessions/active").then((r) => setSessions(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => { clearInterval(iv); clearInterval(t); };
  }, [load]);

  const complete = async (s) => {
    try {
      await api.post(`/staff/complete-session/${s.id}`);
      toast.success("Session completed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const stop = async () => {
    try {
      await api.post(`/staff/stop-session/${stopTarget.id}`, { reason });
      toast.success("Session stopped");
      setStopTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <Center><Loader2 className="h-6 w-6 animate-spin gold-text" /></Center>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-serif text-xl font-bold">Active Sessions <span className="text-gold">({sessions.length})</span></h2>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5 gold-border text-gold"><RefreshCw size={14} /> Refresh</Button>
      </div>
      {sessions.length === 0 ? (
        <EmptyState icon={Timer} text="No active massage sessions right now." />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {sessions.map((s) => {
            const remaining = Math.max(0, Math.floor((new Date(s.ends_at) - Date.now()) / 1000));
            const total = s.duration_minutes * 60;
            const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
            const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
            const ss = String(remaining % 60).padStart(2, "0");
            const done = remaining <= 0;
            return (
              <div key={s.id} className="rounded-2xl bg-card p-6 gold-border" data-testid={`active-session-${s.id}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">{s.customer_name}</span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground"><Armchair size={12} />{s.chair_code}</span>
                </div>
                <div className="relative mx-auto mt-5 flex h-40 w-40 items-center justify-center">
                  <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#27272A" strokeWidth="6" />
                    <circle cx="50" cy="50" r="45" fill="none" stroke={done ? "#10B981" : "#E2B857"} strokeWidth="6"
                      strokeDasharray={`${2 * Math.PI * 45}`} strokeDashoffset={`${2 * Math.PI * 45 * (1 - pct / 100)}`}
                      strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s linear" }} />
                  </svg>
                  <div className="text-center">
                    <div className="font-mono text-3xl font-bold tabular" data-testid={`timer-${s.id}`}>{done ? "00:00" : `${mm}:${ss}`}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{done ? "Completed" : "remaining"}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center gap-3 text-xs text-muted-foreground">
                  <span>{s.duration_minutes} min</span><span>·</span>
                  <span className="flex items-center gold-text"><IndianRupee size={11} />{s.amount}</span>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-2">
                  <Button size="sm" onClick={() => complete(s)} className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`complete-${s.id}`}>
                    <CheckCircle2 size={14} /> Complete
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setStopTarget(s)} className="gap-1.5 border-destructive/50 text-destructive hover:bg-destructive/10" data-testid={`stop-${s.id}`}>
                    <Square size={14} /> Stop
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!stopTarget} onOpenChange={(o) => !o && setStopTarget(null)}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle>Stop Session</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will end {stopTarget?.customer_name}'s session. A reason is required and this action is logged. Refunds are handled separately by admin.</p>
          <div>
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1.5" data-testid="stop-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Customer requested early stop">Customer requested early stop</SelectItem>
                <SelectItem value="Technical problem">Technical problem</SelectItem>
                <SelectItem value="Chair problem">Chair problem</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStopTarget(null)}>Cancel</Button>
            <Button onClick={stop} className="bg-destructive text-white hover:bg-destructive/90" data-testid="confirm-stop-btn">Stop Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status && status !== "all") params.set("status", status);
    api.get(`/staff/orders?${params.toString()}`)
      .then((r) => { setOrders(r.data); setError(null); })
      .catch((e) => setError(apiError(e, "Could not load order history.")))
      .finally(() => setLoading(false));
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const statusChip = (s) => ({
    paid: "bg-emerald-500/15 text-emerald-400",
    pending_payment: "bg-amber-500/15 text-amber-400",
    cancelled: "bg-destructive/15 text-destructive",
    refunded: "bg-violet-500/15 text-violet-400",
  }[s] || "bg-secondary text-muted-foreground");

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-serif text-xl font-bold">Order &amp; Client History <span className="text-gold">({orders.length})</span></h2>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5 gold-border text-gold self-start sm:self-auto"><RefreshCw size={14} /> Refresh</Button>
      </div>
      <div className="mb-5 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name, mobile, code, chair…" className="pl-9" data-testid="history-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-48" data-testid="history-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending_payment">Pending payment</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="history-error">
          <AlertTriangle size={15} /> {error}
        </div>
      )}
      {loading ? (
        <Center><Loader2 className="h-6 w-6 animate-spin gold-text" /></Center>
      ) : orders.length === 0 ? (
        <EmptyState icon={ClipboardCheck} text={q.trim() || status !== "all" ? "No orders match your search." : "No orders yet."} />
      ) : (
        <div className="overflow-x-auto rounded-2xl bg-card gold-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3">Date</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Service</th>
                <th className="p-3">Chair</th>
                <th className="p-3">Code</th>
                <th className="p-3">Eye</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-border/50 last:border-0" data-testid={`history-order-${o.id}`}>
                  <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{new Date(o.confirmed_at || o.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-3"><div className="font-medium">{o.customer_name}</div><div className="text-xs text-muted-foreground">{o.customer_mobile}</div></td>
                  <td className="p-3 whitespace-nowrap text-xs">{o.duration_minutes} min · {o.service_name}</td>
                  <td className="p-3 whitespace-nowrap font-mono text-xs gold-text">{o.chair_code}</td>
                  <td className="p-3 whitespace-nowrap font-mono text-xs">{o.billing_code || "—"}</td>
                  <td className="p-3 whitespace-nowrap text-xs">{o.eye_massager_selected ? <span className="font-semibold text-emerald-400">YES</span> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 whitespace-nowrap text-right font-semibold">₹{o.amount}</td>
                  <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusChip(o.status)}`}>{(o.status || "").replace("_", " ")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Read-only history for verification. Showing up to the 150 most recent matching orders.</p>
    </div>
  );
}

function Center({ children }) { return <div className="flex justify-center py-20">{children}</div>; }

function Row({ l, v }) {
  return <div className="flex justify-between border-b border-border/60 pb-1.5"><span className="text-muted-foreground">{l}</span><span className="font-medium">{v}</span></div>;
}
function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-20 text-center">
      <Icon className="text-muted-foreground/40" size={40} />
      <p className="mt-3 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
